// Slide out class
import { getHtmlElement, getBodyScroll } from "./slideOutUtil.js";
import htmlToElement from "../../js/htmlToElement";

export default class SlideOutPanel {
  constructor(panelId) {
    this.panelId = panelId;
    this.panelSelector = document.querySelector(`#${this.panelId}`);
    this.panelVisible = false;
    this.focusableElementsString = "a[href], "
        + "area[href], "
        + "input:not([disabled]), "
        + "select:not([disabled]),"
        + "textarea:not([disabled]),"
        + "button:not([disabled]),"
        + "iframe,"
        + "object,"
        + "embed,"
        + "*[contenteditable]";

    // Set up elements and event handlers to fix the page tab order
    // See slideOutManager.js for details
    //
    // We add a dummy element at the bottom of the panel that detects when focus is about to
    // enter or leave the panel and redirect focus to the footer as appropriate
    const panelBottomRedirector = htmlToElement(`<div class="fds-footer-bottom-redirector" tabindex="0"></div>`);
    this.panelSelector.append(panelBottomRedirector);

    panelBottomRedirector.addEventListener("focus", (e) => {
      const prevFocusElement = e.relatedTarget;

      if (this.panelSelector.contains(prevFocusElement)) {
        // They're tabbing forwards and just hit the end of the panel
        // Redirect focus to the start of the footer (focus on the first footer link)
        const link = document.querySelector(".govuk-footer a");
        if (link) {
          link.focus();
        }
      } else if (!prevFocusElement) {
        // They're tabbing backwards out of the browser chrome (there's no element in the document that previously had focus)
        // Redirect focus to the end of the footer (focus on the last footer link)
        const footerLinks = document.querySelectorAll(".govuk-footer a");

        if (footerLinks.length > 0) {
          footerLinks[footerLinks.length - 1].focus();
        }
      }
    });

    getHtmlElement().classList.add("fds-slide-out-initialised");
  }

  showPanel() {
    if (this.panelVisible) return;

    this.panelSelector.classList.remove("fds-slide-out-panel--hidden");
    this.panelSelector.classList.add("fds-slide-out-panel--visible");
    this.panelSelector.setAttribute("aria-hidden", "false");

    if (this.panelSelector.classList.contains("fds-slide-out-panel")) {
      getHtmlElement().classList.add("fds-slide-out-visible");
    }

    getBodyScroll().classList.add("fds-body-scroll--with-slide-out");

    getBodyScroll().addEventListener("transitionend", () => {
      // When the transition has finished, disable transitions in case the window is resized
      getBodyScroll().style.transition = "none";
      this.panelSelector.style.transition = "none";
    }, { once: true });

    // Change focus to the panel's close link, so the user is focussed in the panel
    this.focusFirstElement();

    this.panelVisible = true;

    // Replace the fragment identifier in the URL
    if (window.history.replaceState) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set(this.panelId, "true");
      this.historyReplace(searchParams);
    }
  }

  hidePanel(callback) {
    if (!this.panelVisible) return;

    getBodyScroll().style.transition = "";
    this.panelSelector.style.transition = "";
    this.panelSelector.classList.remove("fds-slide-out-panel--visible");
    getHtmlElement().classList.remove("fds-slide-out-visible");

    // Remove body scroll modifier class
    getBodyScroll().classList.remove("fds-body-scroll--with-slide-out");

    getBodyScroll().addEventListener("transitionend", () => {
      getBodyScroll().style.transition = "none";
      getBodyScroll().style.transition = "";
      this.panelSelector.setAttribute("aria-hidden", "true");
      this.panelSelector.classList.add("fds-slide-out-panel--hidden");
    }, { once: true });

    if (typeof callback === "function") callback();

    // Remove the transform so the panel slides back out of view
    this.panelSelector.style.transform = "";
    this.panelVisible = false;

    // Re-focus on the open slide out button once closed
    const openSlideOutButton = document.querySelector(`[data-open-slide-out-panel="${this.panelId}"]`);
    openSlideOutButton.focus();

    // Remove the fragment identifier in the URL
    if (window.history.replaceState) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete(this.panelId);
      this.historyReplace(searchParams);
    }
  }

  historyReplace(searchParams) {
    const searchParamString = searchParams.toString().length > 0 ? `?${searchParams.toString()}` : "";
    const locationHash = window.location.hash.length > 0 ? `#${window.location.hash}` : "";
    window.history.replaceState(undefined, undefined, window.location.pathname + searchParamString + locationHash);
  }

  focusFirstElement() {
    this.panelSelector.querySelectorAll(".fds-slide-out-panel__close")[0].focus();
  }

  focusLastElement() {
    const focusable = this.panelSelector.querySelectorAll(this.focusableElementsString);

    if (focusable.length > 0) {
      focusable[focusable.length - 1].focus();
    }
  }

  focusBottomRedirector() {
    this.panelSelector.querySelector(".fds-footer-bottom-redirector").focus();
  }
}
